import Vue from "vue"
import VueI18n from "vue-i18n"
import { sessionState } from "@/state"
import DynamicProviderKey from "@/model/provider/DynamicProviderKey"
Vue.use(VueI18n)

const defaultMessages = loadDefaultLocaleMessages()

export const i18n = new VueI18n({
    locale: process.env.VUE_APP_I18N_LOCALE || "en",
    fallbackLocale: process.env.VUE_APP_I18N_FALLBACK_LOCALE || "en",
    messages: defaultMessages,
})

function loadDefaultLocaleMessages() {
    const locales = require.context("../i18n/locales", false, /[a-z0-9-_,\s]+\.yaml$/i)
    const messages = {}
    locales.keys().forEach(key => {
        const matched = key.match(/([a-z0-9-_]+)\./i)
        if (matched && matched.length > 1) {
            const locale = matched[1]
            messages[locale] = locales(key)
        }
    })
    return messages
}

function isObject(item) {
    return item && typeof item === "object" && !Array.isArray(item)
}

function deepMerge(target, source) {
    for (const key in source) {
        if (isObject(source[key])) {
            if (!target[key]) {
                target[key] = {};
            }
            deepMerge(target[key], source[key]);
        } else {
            target[key] = source[key];
        }
    }
    return target;
}

export function loadStateLocalesAsync(lang, state) {
    if(state) {
        const getStatesOverrides = require.context("../i18n/locales/state", true, /[a-z0-9-_,\s]+\.yaml$/i)
        const stateMessages = {}
        getStatesOverrides.keys().forEach(key => {
            const lang = key.match(/([a-z0-9-_]+)\./i)
            const matched = key.match(/\.\/([a-z]{2})\//i)
            if (matched[1] === state) {
                const locale = lang[1]
                stateMessages[locale] = getStatesOverrides(key)
                const mergedMessages = deepMerge(defaultMessages, stateMessages)
                i18n.setLocaleMessage(locale, mergedMessages[locale]);
            }
        })
    }
}

// We're monkey-patching the translation functions to do custom key lookups based on group/provider

const tOrig = Vue.prototype.$t
const tcOrig = Vue.prototype.$tc
const unknown = "unknown"

Vue.prototype.$t = function (key, values) {
    const resolvedKey = resolveKey(this, key, values)
    return tOrig.call(this, resolvedKey, values)
}

Vue.prototype.$tc = function (key, choice, values) {
    const resolvedKey = resolveKey(this, key, values)
    return tcOrig.call(this, resolvedKey, choice, values)
}

const keyExistsCache = {}
const keyResolutionCache = {}

function keyExists(scope, key) {
    let cacheValue = keyExistsCache[key]

    if (cacheValue === undefined) {
        cacheValue = keyExistsCache[key] = scope.$te(key)
    }

    return cacheValue
}

function resolveKey(scope, key, values) {
    const experienceType = values?.experienceType || sessionState.experienceType
    const flowType = values?.flowType || sessionState.flowType
    const groupId = values?.groupId || sessionState.session.groupId
    const providerKey = DynamicProviderKey.extractProviderKey(values?.providerKey || sessionState.providerKey)
    const cacheKey = `${groupId || unknown}.${providerKey || unknown}.${flowType || unknown}.${
        experienceType || unknown
    }.${key}`
    const cachedResolvedKey = keyResolutionCache[cacheKey]

    if (cachedResolvedKey) {
        return cachedResolvedKey
    }

    if (groupId && providerKey && flowType && experienceType) {
        const resolvedKey = `${groupId}.${providerKey}.${flowType}.${experienceType}.${key}`
        if (keyExists(scope, resolvedKey)) return keyResolutionCache[cacheKey] = resolvedKey
    }

    if (groupId && providerKey && flowType) {
        const resolvedKey = `${groupId}.${providerKey}.${flowType}.${key}`
        if (keyExists(scope, resolvedKey)) return keyResolutionCache[cacheKey] = resolvedKey
    }

    if (groupId && providerKey && experienceType) {
        const resolvedKey = `${groupId}.${providerKey}.${experienceType}.${key}`
        if (keyExists(scope, resolvedKey)) return keyResolutionCache[cacheKey] = resolvedKey
    }

    if (groupId && providerKey) {
        const resolvedKey = `${groupId}.${providerKey}.${key}`
        if (keyExists(scope, resolvedKey)) return keyResolutionCache[cacheKey] = resolvedKey
    }

    if (providerKey && flowType && experienceType) {
        const resolvedKey = `${providerKey}.${flowType}.${experienceType}.${key}`
        if (keyExists(scope, resolvedKey)) return keyResolutionCache[cacheKey] = resolvedKey
    }

    if (providerKey && flowType) {
        const resolvedKey = `${providerKey}.${flowType}.${key}`
        if (keyExists(scope, resolvedKey)) return keyResolutionCache[cacheKey] = resolvedKey
    }

    if (providerKey && experienceType) {
        const resolvedKey = `${providerKey}.${experienceType}.${key}`
        if (keyExists(scope, resolvedKey)) return keyResolutionCache[cacheKey] = resolvedKey
    }

    if (providerKey) {
        const resolvedKey = `${providerKey}.${key}`
        if (keyExists(scope, resolvedKey)) return keyResolutionCache[cacheKey] = resolvedKey
    }

    if (groupId && flowType && experienceType) {
        const resolvedKey = `${groupId}.${flowType}.${experienceType}.${key}`
        if (keyExists(scope, resolvedKey)) return keyResolutionCache[cacheKey] = resolvedKey
    }

    if (groupId && flowType) {
        const resolvedKey = `${groupId}.${flowType}.${key}`
        if (keyExists(scope, resolvedKey)) return keyResolutionCache[cacheKey] = resolvedKey
    }

    if (groupId && experienceType) {
        const resolvedKey = `${groupId}.${experienceType}.${key}`
        if (keyExists(scope, resolvedKey)) return keyResolutionCache[cacheKey] = resolvedKey
    }

    if (groupId) {
        const resolvedKey = `${groupId}.${key}`
        if (keyExists(scope, resolvedKey)) return keyResolutionCache[cacheKey] = resolvedKey
    }

    if (flowType && experienceType) {
        const resolvedKey = `${flowType}.${experienceType}.${key}`
        if (keyExists(scope, resolvedKey)) return keyResolutionCache[cacheKey] = resolvedKey
    }

    if (flowType) {
        const resolvedKey = `${flowType}.${key}`
        if (keyExists(scope, resolvedKey)) return keyResolutionCache[cacheKey] = resolvedKey
    }

    if (experienceType) {
        const resolvedKey = `${experienceType}.${key}`
        if (keyExists(scope, resolvedKey)) return keyResolutionCache[cacheKey] = resolvedKey
    }

    keyResolutionCache[cacheKey] = key

    return key
}
